import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FaCheckCircle, FaBox, FaTruck, FaCreditCard, FaMapMarkerAlt, FaEnvelope, FaUser } from 'react-icons/fa';
import { formatPrice } from '../utils/currency';

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const { i18n } = useTranslation();
  const { API } = useContext(AppContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statuses = {
      pending: {
        label: i18n.language === 'fr' ? 'En attente de confirmation' : 'Pending confirmation',
        color: 'text-orange-600',
        bg: 'bg-[#00D4FF]/10',
        icon: '⏳'
      },
      confirmed: {
        label: i18n.language === 'fr' ? 'Confirmée' : 'Confirmed',
        color: 'text-blue-600',
        bg: 'bg-blue-100',
        icon: '✓'
      },
      processing: {
        label: i18n.language === 'fr' ? 'En préparation' : 'Processing',
        color: 'text-blue-600',
        bg: 'bg-blue-100',
        icon: '📦'
      },
      shipped: {
        label: i18n.language === 'fr' ? 'Expédiée' : 'Shipped',
        color: 'text-purple-600',
        bg: 'bg-purple-100',
        icon: '🚚'
      },
      delivered: {
        label: i18n.language === 'fr' ? 'Livrée' : 'Delivered',
        color: 'text-[#635BFF]',
        bg: 'bg-[#635BFF]/10',
        icon: '✅'
      },
      completed: {
        label: i18n.language === 'fr' ? 'Terminée' : 'Completed',
        color: 'text-[#635BFF]',
        bg: 'bg-[#635BFF]/10',
        icon: '✅'
      }
    };
    return statuses[status] || statuses.pending;
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      cash: i18n.language === 'fr' ? 'Espèces' : 'Cash',
      card: i18n.language === 'fr' ? 'Carte bancaire' : 'Credit Card',
      bank_transfer: i18n.language === 'fr' ? 'Virement bancaire' : 'Bank Transfer',
      delivery: i18n.language === 'fr' ? 'Paiement à la livraison' : 'Payment on Delivery'
    };
    return methods[method] || method;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2540]"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-4">
            {i18n.language === 'fr' ? 'Commande introuvable' : 'Order not found'}
          </h2>
          <Link to="/shop">
            <Button className="bg-[#0A2540] hover:bg-[#003d56]">
              {i18n.language === 'fr' ? 'Retour à la boutique' : 'Back to shop'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(order.status);

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#635BFF]/10 rounded-full mb-4">
            <FaCheckCircle className="text-5xl text-[#635BFF]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-2">
            {i18n.language === 'fr' ? 'Commande confirmée !' : 'Order Confirmed!'}
          </h1>
          <p className="text-gray-600">
            {i18n.language === 'fr' 
              ? 'Merci pour votre commande. Vous recevrez un email de confirmation.' 
              : 'Thank you for your order. You will receive a confirmation email.'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Order Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Info Card */}
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-[#0A2540] mb-1">
                      {i18n.language === 'fr' ? 'Commande' : 'Order'} #{order.id.substring(0, 8).toUpperCase()}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {new Date(order.created_at).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
                    </p>
                  </div>
                  <div className={`px-4 py-2 rounded-full ${statusInfo.bg}`}>
                    <span className={`font-semibold ${statusInfo.color}`}>
                      {statusInfo.icon} {statusInfo.label}
                    </span>
                  </div>
                </div>

                {/* Order Items */}
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <FaBox className="text-[#0A2540]" />
                  {i18n.language === 'fr' ? 'Articles commandés' : 'Ordered Items'}
                </h3>
                <div className="space-y-4">
                  {order.items?.map((item, index) => (
                    <div key={index} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-gray-500">
                          {i18n.language === 'fr' ? 'Quantité' : 'Quantity'}: {item.quantity} • 
                          {item.type === 'new' ? (i18n.language === 'fr' ? ' Neuf' : ' New') :
                           item.type === 'used' ? (i18n.language === 'fr' ? ' Occasion' : ' Used') :
                           (i18n.language === 'fr' ? ' Location' : ' Rental')}
                        </p>
                      </div>
                      <p className="font-bold text-[#0A2540]">
                        {formatPrice(item.price * item.quantity, 'EUR')}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Delivery & Payment Info */}
            <Card>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Delivery Address */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <FaMapMarkerAlt className="text-[#0A2540]" />
                      {i18n.language === 'fr' ? 'Adresse de livraison' : 'Delivery Address'}
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="flex items-center gap-2 mb-2">
                        <FaUser className="text-gray-400" />
                        {order.user_name}
                      </p>
                      <p className="flex items-center gap-2 mb-2">
                        <FaEnvelope className="text-gray-400" />
                        {order.user_email}
                      </p>
                      <p className="text-gray-700">{order.delivery_address}</p>
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <FaCreditCard className="text-[#0A2540]" />
                      {i18n.language === 'fr' ? 'Mode de paiement' : 'Payment Method'}
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-medium">{getPaymentMethodLabel(order.payment_method)}</p>
                      {order.payment_method === 'bank_transfer' && (
                        <div className="mt-3 text-sm text-gray-600">
                          <p className="font-semibold mb-1">
                            {i18n.language === 'fr' ? 'Coordonnées bancaires :' : 'Bank details:'}
                          </p>
                          <p>IBAN: FR76 XXXX XXXX XXXX XXXX XXXX XXX</p>
                          <p>BIC: XXXXXXXX</p>
                          <p className="mt-2 text-xs">
                            {i18n.language === 'fr' 
                              ? `Référence: CMD-${order.id.substring(0, 8).toUpperCase()}`
                              : `Reference: ORD-${order.id.substring(0, 8).toUpperCase()}`}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary Sidebar */}
          <div>
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h3 className="font-bold text-xl text-[#0A2540] mb-4">
                  {i18n.language === 'fr' ? 'Récapitulatif' : 'Summary'}
                </h3>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {i18n.language === 'fr' ? 'Sous-total' : 'Subtotal'}
                    </span>
                    <span>{formatPrice(order.total - (order.delivery_cost || 0), 'EUR')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {i18n.language === 'fr' ? 'Livraison' : 'Delivery'}
                    </span>
                    <span>{formatPrice(order.delivery_cost || 0, 'EUR')}</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between text-lg font-bold">
                      <span>{i18n.language === 'fr' ? 'Total' : 'Total'}</span>
                      <span className="text-[#0A2540]">{formatPrice(order.total, 'EUR')}</span>
                    </div>
                  </div>
                </div>

                {/* Estimated Delivery */}
                <div className="bg-blue-50 p-4 rounded-lg mb-6">
                  <div className="flex items-center gap-2 text-blue-700 mb-2">
                    <FaTruck />
                    <span className="font-semibold">
                      {i18n.language === 'fr' ? 'Livraison estimée' : 'Estimated Delivery'}
                    </span>
                  </div>
                  <p className="text-blue-600">
                    {(() => {
                      const deliveryDate = new Date();
                      deliveryDate.setDate(deliveryDate.getDate() + 5);
                      return deliveryDate.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      });
                    })()}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Link to="/user/orders" className="block">
                    <Button className="w-full bg-[#0A2540] hover:bg-[#003d56]">
                      {i18n.language === 'fr' ? 'Voir mes commandes' : 'View my orders'}
                    </Button>
                  </Link>
                  <Link to="/shop" className="block">
                    <Button variant="outline" className="w-full">
                      {i18n.language === 'fr' ? 'Continuer mes achats' : 'Continue shopping'}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
